import { Flex } from '@chakra-ui/react';
import { AlertTriangle, CheckCircle2, Clock } from 'lucide-react';
import React, { useMemo } from 'react';
import { OktonChart as Chart } from '@okton/platform-ui/charts';
import { DadosDashboard } from '../../../services/hooks/useDashboard';
import { formatPercentual } from '../formatacao';
import CargaGrafico from './CargaGrafico';
import ChartInfo from './ChartInfo';
import { useCoresDashboard } from './cores';

export type Situacao = 'conciliados' | 'pendentes' | 'divergentes';

interface Props {
  dados?: DadosDashboard;
  isLoading: boolean;
  isError: boolean;
  error?: unknown;
  onRetry: () => void;
  onAbrir: (situacao: Situacao) => void;
  atualizando?: boolean;
}

const TITULO = 'Situação dos Lançamentos';
const ALTURA = '420px';

const SituacaoChart: React.FC<Props> = ({
  dados,
  isLoading,
  isError,
  error,
  onRetry,
  onAbrir,
  atualizando,
}) => {
  const cores = useCoresDashboard();
  const total = dados?.extratototal ?? 0;
  const divergentes = dados?.divergencias ?? 0;
  // O conciliado com divergência aparece só como divergente: as três fatias
  // somam o extrato do período.
  const fatias = [
    {
      chave: 'conciliados' as const,
      Situacao: 'Conciliados',
      Quantidade: (dados?.extratoconciliado ?? 0) - divergentes,
      cor: cores.conciliado,
      icon: <CheckCircle2 size={20} color={cores.conciliado} />,
    },
    {
      chave: 'pendentes' as const,
      Situacao: 'Pendentes',
      Quantidade: dados?.extratopendente ?? 0,
      cor: cores.pendente,
      icon: <Clock size={20} color={cores.pendente} />,
    },
    {
      chave: 'divergentes' as const,
      Situacao: 'Divergentes',
      Quantidade: divergentes,
      cor: cores.divergente,
      icon: <AlertTriangle size={20} color={cores.divergente} />,
    },
  ];
  // Só nome e quantidade, memorizados: o recharts anima a troca de filtro e
  // não reanima um refetch que devolveu o mesmo número.
  const quantidades = fatias.map(f => f.Quantidade).join('|');
  const dadosGrafico = useMemo(
    () =>
      total > 0
        ? fatias
            .filter(f => f.Quantidade > 0)
            .map(f => ({ Situacao: f.Situacao, Quantidade: f.Quantidade }))
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [total, quantidades],
  );
  // Na mesma ordem e com o mesmo filtro dos dados: situação zerada não vira
  // fatia nem barra, e a cor não pode escorregar para a vizinha.
  const coresDasFatias = fatias.filter(f => f.Quantidade > 0).map(f => f.cor);
  const percentual = (valor: number) =>
    formatPercentual(total > 0 ? (valor * 100) / total : 0);

  return (
    <CargaGrafico
      atualizando={atualizando}
      titulo={TITULO}
      isLoading={isLoading}
      isError={isError}
      error={error}
      onRetry={onRetry}
      h={ALTURA}
    >
      <Chart
        h={ALTURA}
        title={TITULO}
        // Sem extrato no período o Chart mostra o "Sem Dados" dele.
        data={dadosGrafico}
        format="pieTotal"
        rosca
        formatosPermitidos={['pieTotal', 'bar']}
        // Cada situação com a sua cor na pizza e nas barras. Sem
        // corPorBarra, a série única (Quantidade) pintava as três barras de
        // verde. O primeiro item é a cor da série, convenção do corPorBarra.
        corPorBarra
        graphColor={[cores.conciliado, ...coresDasFatias]}
        dataKeys={['Quantidade']}
        nameKey="Situacao"
        // Número e percentual escritos ao lado: a situação não depende só da
        // cor da fatia para ser lida.
        rightInfo={
          <Flex flexDir="column" w="40%" gridGap="2" justify="center">
            {fatias.map(f => (
              <ChartInfo
                key={f.chave}
                titulo={f.Situacao}
                valor={`${f.Quantidade.toLocaleString('pt-BR')} (${percentual(
                  f.Quantidade,
                )})`}
                corValor={f.cor}
                icon={f.icon}
                onClick={() => onAbrir(f.chave)}
              />
            ))}
          </Flex>
        }
      />
    </CargaGrafico>
  );
};

export default SituacaoChart;
